import { Component, DoCheck, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { FunctionalityCode } from 'src/app/core/_enums/functionality.code';
import { CustomIndustryRequest } from 'src/app/core/_models/customIndustryRequest';
import { FdesCalcModelVersion } from 'src/app/core/_models/fdesCalcModelVersion';
import { AuthService } from 'src/app/core/_services/auth.service';
import { FdesCalcModelService } from 'src/app/core/_services/fdes-calc-model.service';
import { IndustrieService } from 'src/app/core/_services/industrie.service';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { DialogRejectCustomIndustryRequestComponent } from '../shared/dialog-reject-custom-industry-request/dialog-reject-custom-industry-request.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-industrie-validation',
  templateUrl: './industrie-validation.component.html',
  styleUrls: ['./industrie-validation.component.scss']
})
export class IndustrieValidationComponent implements OnInit, DoCheck {
  acuView = false;
  canValidateCustomIndustry = false;
  canManageFdesCalcModel = false;
  isFrance = true;
  showCalcModelTab = true;
  tab: 'requests' | 'engine' = 'requests';
  private initialized = false;

  demandes: CustomIndustryRequest[] = [];
  historique: CustomIndustryRequest[] = [];
  // position de la liste quand on ouvre une demande, rendue au retour
  private listScrollTop = 0;
  selectedDemande: CustomIndustryRequest | null = null;

  engineVersionInput = '';
  engineVersions: FdesCalcModelVersion[] = [];

  engineFile: File | null = null;
  engineFileName = '';
  // one line per rejected rule (the API returns every failure at once) — shown under the upload zone
  engineErrors: string[] = [];
  engineChecking = false;
  enginePublishing = false;

  @ViewChild('engineFileInput') engineFileInputRef!: ElementRef<HTMLInputElement>;

  constructor(
    private authService: AuthService,
    private industrieService: IndustrieService,
    private fdesCalcModelService: FdesCalcModelService,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.refreshPermissions();
  }

  // Permissions (authService.accessesValues) may not be loaded yet on a fresh page reload — re-read them
  // every change-detection cycle, and run the one-time init (default tab + load) as soon as they arrive.
  // Otherwise the view stays hidden on reload until the user navigates away and back.
  ngDoCheck(): void {
    this.refreshPermissions();
  }

  private refreshPermissions(): void {
    if (!this.authService?.accessesValues) return;
    this.acuView = this.authService.accessesValues.includes(FunctionalityCode.ACUView);
    this.canValidateCustomIndustry = this.authService.accessesValues.includes(FunctionalityCode.ValidateCustomIndustry);
    this.canManageFdesCalcModel = this.authService.accessesValues.includes(FunctionalityCode.ManageFdesCalcModel);
    // Ouvert temporairement à toutes les régions pour recetter la V2 en préprod.
    this.isFrance = true;
    if (!this.initialized && (this.acuView || this.canValidateCustomIndustry || this.canManageFdesCalcModel)) {
      this.initialized = true;
      this.tab = this.acuView || this.canValidateCustomIndustry ? 'requests' : 'engine';
      if (this.isFrance && (this.canValidateCustomIndustry || this.acuView)) {
        this.loadPendingRequests();
      }
      if (this.isFrance && this.canManageFdesCalcModel) {
        this.loadEngineVersions();
      }
    }
  }

  switchToFrance() {
    this.authService.switchRegion('1');
  }

  get pendingCount(): number {
    return this.demandes.length;
  }

  loadPendingRequests() {
    this.industrieService.getPendingCustomIndustryRequests().subscribe((data) => {
      this.demandes = data;
    });
    this.industrieService.getCustomIndustryRequestsHistory().subscribe((data) => {
      this.historique = data;
    });
  }

  // Le détail et la liste se succèdent dans le même conteneur défilant, celui de la page (.outlet) :
  // sans ce retour en haut, une demande choisie en bas de liste s'ouvre au milieu de son écran.
  private get scrollContainer(): Element | null {
    return document.querySelector('.outlet');
  }

  selectDemande(demande: CustomIndustryRequest) {
    this.listScrollTop = this.scrollContainer?.scrollTop ?? 0;
    this.selectedDemande = demande;
    this.scrollContainer?.scrollTo({ top: 0 });
    // fetch the full detail (includes the before -> after base-info diff, not present in the list)
    if (demande.customIndustryValuesId) {
      this.industrieService.getCustomIndustryRequest(demande.customIndustryValuesId).subscribe((full) => {
        // une demande annulée n'est plus servie par le détail : sa ligne d'historique porte déjà tout
        // ce que l'écran affiche, sauf le diff des informations de base
        if (full && this.selectedDemande?.customIndustryValuesId === full.customIndustryValuesId) {
          this.selectedDemande = full;
        }
      });
    }
  }

  // Per-field before/after of the requested base info, keyed for a form-like layout. `changed` is true
  // only when there's a "current" value (a modification) that differs — the old value is then struck out
  // next to the new one. For a creation (no current) nothing is struck: it just shows the requested values.
  get baseDiff() {
    const d = this.selectedDemande;
    const cur = d?.current;
    const fmt = (v: any) => (v == null || v === '' ? '—' : String(v));
    const fmtBool = (v: any) => (v == null ? '—' : v ? 'Oui' : 'Non');
    const field = (before: any, after: any, isBool = false) => {
      const b = isBool ? fmtBool(before) : fmt(before);
      const a = isBool ? fmtBool(after) : fmt(after);
      return { before: b, after: a, changed: !!cur && b !== a };
    };
    return {
      displayName: field(cur?.displayName, d?.displayName),
      code: field(cur?.code, d?.code),
      family: field(cur?.familyDisplayName, d?.familyDisplayName),
      addressL3: field(cur?.addressL3, d?.addressL3),
      addressL4: field(cur?.addressL4, d?.addressL4),
      addressL5: field(cur?.addressL5, d?.addressL5),
      addressPC: field(cur?.addressPC, d?.addressPC),
      addressLocation: field(cur?.addressLocation, d?.addressLocation),
      mobileStation: field(cur?.mobileStation, d?.mobileStation, true)
    };
  }

  // files are served directly by the API's static DocumentPath mount (Startup.cs: /Documents),
  // same as every other uploaded document in this app — no dedicated download endpoint needed
  downloadFile(relativePath?: string): void {
    if (!relativePath) return;
    window.open(`${environment.baseUrl}Documents/${relativePath}`, '_blank');
  }

  formatFileSize(bytes?: number): string {
    if (bytes == null) return '';
    if (bytes < 1024 * 1024) {
      return `${Math.round(bytes / 1024)} Ko`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo`;
  }

  closeDemande() {
    this.selectedDemande = null;
    // la liste n'est réaffichée qu'au cycle suivant : elle n'a pas encore de hauteur à ce moment-là,
    // et un repositionnement immédiat serait ramené à zéro
    setTimeout(() => this.scrollContainer?.scrollTo({ top: this.listScrollTop }));
  }

  validateDemande() {
    if (!this.selectedDemande?.customIndustryValuesId) {
      return;
    }
    this.industrieService.validateCustomIndustryRequest(this.selectedDemande.customIndustryValuesId).subscribe(() => {
      this.snackbarService.open(SnackbarTypes.update);
      this.closeDemande();
      this.loadPendingRequests();
    });
  }

  refuseDemande() {
    if (!this.selectedDemande?.customIndustryValuesId) {
      return;
    }
    const requestId = this.selectedDemande.customIndustryValuesId;
    const ref = this.dialog.open(DialogRejectCustomIndustryRequestComponent, {
      data: { industryDisplayName: this.selectedDemande.industryDisplayName }
    });

    ref.afterClosed().subscribe((comment: string | undefined) => {
      if (!comment) {
        return;
      }
      this.industrieService.rejectCustomIndustryRequest(requestId, comment).subscribe(() => {
        this.snackbarService.open(SnackbarTypes.update);
        this.closeDemande();
        this.loadPendingRequests();
      });
    });
  }

  loadEngineVersions() {
    this.fdesCalcModelService.getVersions().subscribe((versions) => {
      this.engineVersions = versions;
    });
  }

  get activeEngineVersion(): FdesCalcModelVersion | undefined {
    return this.engineVersions.find((v) => v.isActive);
  }

  downloadEngineVersion(version: FdesCalcModelVersion) {
    this.fdesCalcModelService.downloadFile(version.fdesCalculationModelId).subscribe((file) => {
      const blob = new Blob([file], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const downloadLink = document.createElement('a');
      downloadLink.href = window.URL.createObjectURL(blob);
      downloadLink.setAttribute('download', version.originalFileName);
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
    });
  }

  get canPublishEngine(): boolean {
    return !!this.engineFile && !!this.engineVersionInput?.trim() && !this.engineChecking && !this.enginePublishing;
  }

  attachEngineFile() {
    this.engineFileInputRef?.nativeElement.click();
  }

  // check the workbook as soon as it is picked: the admin sees what to fix before typing a version
  onEngineFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) {
      return;
    }

    this.engineFile = file;
    this.engineFileName = file.name;
    this.engineErrors = [];
    this.engineChecking = true;

    this.fdesCalcModelService.validate(file).subscribe({
      next: () => (this.engineChecking = false),
      error: (err) => {
        this.engineChecking = false;
        this.engineErrors = this.toErrorLines(err);
      }
    });
  }

  publishEngineVersion() {
    const version = this.engineVersionInput?.trim();
    if (!version || !this.engineFile) {
      return;
    }

    this.engineErrors = [];
    this.enginePublishing = true;

    this.fdesCalcModelService.publish(version, this.engineFile).subscribe({
      next: () => {
        this.enginePublishing = false;
        this.snackbarService.open(this.translate.instant('industrie_validation_engine_published'));
        this.resetEngineUpload();
        this.loadEngineVersions();
      },
      error: (err) => {
        this.enginePublishing = false;
        this.engineErrors = this.toErrorLines(err);
      }
    });
  }

  private resetEngineUpload() {
    this.engineFile = null;
    this.engineFileName = '';
    this.engineVersionInput = '';
    if (this.engineFileInputRef) {
      this.engineFileInputRef.nativeElement.value = '';
    }
  }

  // the interceptor joins the API's validation failures with newlines — split them back into a list so
  // every rejected rule is visible at once
  private toErrorLines(err: any): string[] {
    const message = typeof err === 'string' && err.trim() ? err : this.translate.instant('snackbar_error');
    return message
      .split('\n')
      .map((line: string) => line.trim())
      .filter((line: string) => !!line);
  }
}
